<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { trackInsightEvent } from '@/api/tracking';
import { DgrIcon, DgrLoading } from '@stockmarkteam/donguri-ui';
import { useInsightPrefillStatuses } from '@/components/home/insights/use-insight-prefill-statuses';
import { useInsightTracking } from '@/components/home/insights/use-insight-tracking';
import { insightAgentPresentations } from '@/components/insights/insight-action-display';
import type { InsightRecommendedAction } from '@/types/insights';
import {
  INSIGHT_ACTION_ID_QUERY_KEY,
  INSIGHT_ACTION_UUID_QUERY_KEY,
} from '@/utils/composables/useInsightActionPrefill';
import { useLayoutVariant } from '@/utils/composables/useLayoutVariant';

interface Props {
  action: InsightRecommendedAction;
  hintId: number;
  disabled?: boolean;
  disabledReason?: string;
}
const props = defineProps<Props>();

const router = useRouter();

const { pageName, insightId, axisId, axisName } = useInsightTracking();
const layoutVariant = useLayoutVariant();

const agent = computed(
  () => insightAgentPresentations[props.action.agent_type],
);

const trackOpen = () => {
  trackInsightEvent(agent.value.trackingEventName, {
    page: { name: pageName },
    insight: { id: insightId },
    axis: { id: axisId, name: axisName },
    hint: { id: props.hintId },
    recommended_action: { id: props.action.id },
    layout_variant: layoutVariant,
  });
};

const { resolveStatus } = useInsightPrefillStatuses();
const status = computed(
  () =>
    resolveStatus(props.action.uuid) ??
    props.action.prefill_prompt_generation_status,
);
const isLoading = computed(() => status.value === 'processing');

const handleClick = () => {
  if (isLoading.value || props.disabled) return;

  trackOpen();

  const location = {
    name: agent.value.routeName,
    query: {
      [INSIGHT_ACTION_UUID_QUERY_KEY]: props.action.uuid,
      [INSIGHT_ACTION_ID_QUERY_KEY]: String(props.action.id),
      from: 'insight',
    },
  };

  if (agent.value.opensInNewTab) {
    window.open(router.resolve(location).href, '_blank', 'noopener,noreferrer');
  } else {
    router.push(location);
  }
};
</script>

<template>
  <!--
    disabled 属性だと Safari などで hover イベントが発火せず
    無効理由のツールチップが出ないため、disabled prop は aria-disabled で表現する。
  -->
  <button
    v-tooltip="{ content: disabledReason }"
    type="button"
    class="insight-action-card"
    :disabled="isLoading"
    :aria-disabled="disabled || undefined"
    @click="handleClick"
  >
    <div class="action-info">
      <div class="agent c-text-regular--s">
        {{ agent.label }}
      </div>
      <div class="action-detail c-text-regular--m">
        <DgrLoading v-if="isLoading" size="small" />
        <DgrIcon v-else :name="agent.icon" size="small" :keep-fill="false" />
        {{ action.label }}
      </div>
    </div>
    <DgrIcon
      class="link-icon"
      :name="agent.linkIcon"
      size="small"
      :keep-fill="false"
    />
  </button>
</template>

<style lang="scss" scoped>
.insight-action-card {
  display: flex;
  gap: 24px;
  align-items: center;
  justify-content: space-between;
  text-align: start;
  background: #fff;
  height: auto;
  border: none;
  border-radius: 4px;
  padding: 8px;

  &:disabled,
  &[aria-disabled='true'] {
    color: $color-gray600;
    cursor: default;

    &:hover {
      background: #fff;
      border: none;
    }
  }

  &:not(:disabled):not([aria-disabled='true']):hover {
    background-color: $color-gray400;
  }

  .action-info {
    align-self: flex-start;
  }

  .link-icon {
    flex-shrink: 0;
  }

  &:not(:disabled):not([aria-disabled='true']) .agent {
    color: $color-gray800;
  }
  .action-detail {
    display: flex;
    gap: 2px;

    .icon-box {
      flex-shrink: 0;
      padding: 3px;
    }
  }
}
</style>
